import { readContracts } from '@wagmi/core'
import moment from 'moment'
import axios from 'axios'

import { getWagmiConfig } from '../components/walletConnect/wagmiConfig'
import { getAllContractsForClaim } from '../constants/claims'
import { vestingAbi } from '../abis/vestingAbi'
import {
  floorNumber,
  formatAmount,
  formatCurrency
} from '../util/numbers.helpers'
import { Vesting } from '../util/vesting.helpers'
import { stakingAbi } from '../abis/stakingAbi'
import {
  CURRENCIES,
  CLAIM_CURRENCY_TYPE,
  CONTRACT_ID_FOR_CUSTOM_FLOW
} from '../constants/app'
import { getCustomVestClaimAPIUrl } from './api.helpers'
import { isCustomVestingFlow } from './app.helpers'

const getContractsAddresses = () => {
  const contractAddresses = process.env.REACT_APP_CONTRACTS
  return contractAddresses ? JSON.parse(contractAddresses) : {}
}

const stakingContractConfig = () => {
  return {
    address: getContractsAddresses()?.staking,
    abi: stakingAbi
  }
}

const stakingContractConfig2 = () => {
  return {
    address: getContractsAddresses()?.staking2
  }
}

const formatVestingData = ({ total, claimed, available, ...restProps }) => {
  return {
    total: formatAmount(total),
    claimed: formatAmount(claimed),
    available: formatAmount(available),
    ...restProps
  }
}

const buildReadFromContract = async (
  { id, userAddress },
  { address, ...restProps }
) => {
  const contractConfig = {
    address,
    abi: vestingAbi
  }
  const response = await readContracts(getWagmiConfig, {
    contracts: [
      {
        ...contractConfig,
        functionName: 'amounts',
        args: [userAddress]
      },

      {
        ...contractConfig,
        functionName: 'released',
        args: [userAddress]
      },

      {
        ...contractConfig,
        functionName: 'releaseable',
        args: [userAddress]
      }
    ]
  })
  const [total, claimed, available] = response
  return formatVestingData({
    id,
    address,
    total: total?.result,
    claimed: claimed?.result,
    available: available?.result,
    ...restProps
  })
}

const buildReadFromStakingContract = async (userAddress) => {
  const contractConfig = stakingContractConfig()
  const response = await readContracts(getWagmiConfig, {
    contracts: [
      {
        ...contractConfig,
        functionName: 'amounts',
        args: [userAddress]
      }
      // {
      //   ...contractConfig,
      //   functionName: 'releaseable',
      //   args: [userAddress]
      // }
    ]
  })
  // const [available, total] = response
  const [available] = response
  return {
    // total: formatAmount(total?.result),
    available: formatAmount(available?.result)
  }
}

const buildReadFromQuestTapAPI = async (
  { id, userAddress },
  { address, ...restProps }
) => {
  const result = {
    choice: '',
    total: 0,
    claimed: 0,
    available: 0
  }
  try {
    const { data } = await axios.get(
      getCustomVestClaimAPIUrl(CONTRACT_ID_FOR_CUSTOM_FLOW[id], userAddress)
    )
    const { amounts, released, releaseable, choice } = data
    result.total = amounts
    result.claimed = released
    result.available = releaseable
    result.choice = choice
  } catch (e) {
    console.log(e)
  }
  return formatVestingData({
    id,
    address,
    ...result,
    ...restProps
  })
}

const buildContractsByAvailability = (userAddress, filteredIds = []) => {
  const claimContracts = getAllContractsForClaim()
  const claimContractIds = filteredIds.length
    ? filteredIds
    : Object.keys(claimContracts)
  return claimContractIds.map((id) => {
    if (isCustomVestingFlow(id)) {
      return buildReadFromQuestTapAPI({ id, userAddress }, claimContracts[id])
    }
    return buildReadFromContract({ id, userAddress }, claimContracts[id])
  })
}

const calculateAvailableBalance = (contracts) => {
  return contracts.map(({ ...restProps }) => {
    const { total, start, cliff, duration, tge, claimed } = restProps
    const vesting = new Vesting(start, duration, cliff, tge, total)
    const vestedAmount = vesting.vestedAmount()
    return {
      ...restProps,
      available: vestedAmount - claimed
    }
  })
}

const filterContractsForCalculation = (contracts) => {
  return contracts.filter(({ available }) => available > 0)
}

const filterAvailableBalanceById = (contractsForClaim, currentId) => {
  return contractsForClaim?.filter(({ id }) => id === currentId)?.[0]?.available
}

const nextClaimTime = () => {
  const currentDate = moment.utc()
  // Get the next Thursday at 8 AM UTC
  let nextWeek = moment
    .utc()
    .day('Thursday')
    .hour(8)
    .minute(0)
    .second(0)
    .millisecond(0)
  if (nextWeek.isBefore(currentDate)) {
    nextWeek = nextWeek.add(7, 'days')
  }
  // Calculate the time delta
  return formatTimeDelta(nextWeek.diff(currentDate))
}

const formatTimeDelta = (timeDelta) => {
  // Convert milliseconds to days, hours, and minutes
  const days = Math.floor(timeDelta / (1000 * 60 * 60 * 24))
  const hours = Math.floor(
    (timeDelta % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  )
  const minutes = Math.floor((timeDelta % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((timeDelta % (1000 * 60)) / 1000)

  // Format the result
  return `${days}d ${hours}h ${minutes}m ${seconds}s`
}

const sumAvailableClaimStats = (data) => {
  return data.reduce((total, { available = 0 }) => total + available, 0)
}

const qbxToVir = (value) => {
  return `${formatCurrency(value ? floorNumber(value) : 0)} ${CURRENCIES.VIR}`
}

const buildCurrencyByChoice = (choice) => {
  if (choice === CLAIM_CURRENCY_TYPE.QBX) {
    return CURRENCIES.QBX
  } else if (choice === CLAIM_CURRENCY_TYPE.VIR) {
    return CURRENCIES.VIR
  } else {
    return CURRENCIES.QBX
  }
}

export {
  getContractsAddresses,
  stakingContractConfig,
  stakingContractConfig2,
  buildReadFromContract,
  buildContractsByAvailability,
  calculateAvailableBalance,
  filterContractsForCalculation,
  filterAvailableBalanceById,
  nextClaimTime,
  buildReadFromStakingContract,
  sumAvailableClaimStats,
  qbxToVir,
  buildCurrencyByChoice
}
